import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";

import TextInput from "../../../components/misc/TextInput";
import ContentCard from "../../../components/misc/ContentCard";

import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";
import {
  getCurrentReason,
  postCreateOrUpdateReason,
} from "../../../redux/api/admin.api";
import { clearCurrentReason } from "../../../redux/reducer/admin.reducer";
import useRoutesHelper from "../../../hooks/useRoutesHelper";
import FormButtons from "../../../components/misc/FormButtons";
import Dropdown from "../../../components/misc/Dropdown";
import Checkbox from "../../../components/misc/Checkbox";
import { administrateReasonsTypes } from "../../../constants/statuses";
import { operationsTypes } from "./components/tableConfigs/administrateReasonTableConfig";

const initialSelectedReasonType = administrateReasonsTypes[0].value;
const initialSelectedOperationType = operationsTypes[0].value;

interface initialValuesInterface {
  denumire: string;
  selectedReasonType: string | number;
  selectedOperationType: string | number;
  showReceiver: boolean;
  showProvider: boolean;
}

const initialValues = {
  denumire: "",
  selectedReasonType: initialSelectedReasonType,
  selectedOperationType: initialSelectedOperationType,
  showReceiver: false,
  showProvider: false,
};

const validationSchema = Yup.object({
  denumire: Yup.string().required("Denumirea este obligatorie"),
  selectedReasonType: Yup.string().required("Tipul este obligatoriu"),
  selectedOperationType: Yup.string().required("Operatia este obligatorie"),
});

function CreateOrUpdateReason() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { routeMainParam } = useRoutesHelper();

  const { reasonId } = routeMainParam as { reasonId: string };

  const {
    admin: {
      currentReason: { data: reasonData, isLoading },
    },
  } = useAppSelector((state) => ({
    admin: state.admin,
  }));

  const [formikValues, setFormikValues] =
    useState<initialValuesInterface>(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (reasonId) {
      dispatch(
        getCurrentReason({
          idReason: reasonId,
        }),
      );
    }
  }, [dispatch, reasonId]);

  useEffect(() => {
    return () => {
      dispatch(clearCurrentReason());
    };
  }, [dispatch]);

  useEffect(() => {
    if (reasonId && reasonData) {
      setFormikValues((prev) => ({
        ...prev,
        denumire: reasonData.denumire,
        selectedReasonType: reasonData.type,
        selectedOperationType: reasonData.operatie,
        showProvider: !!reasonData.viz_exp,
        showReceiver: !!reasonData.viz_dest,
      }));
    }
  }, [reasonId, reasonData]);

  const handleSaveReasonDetails = async (value: initialValuesInterface) => {
    setIsSubmitting(true);

    const resultAction = await dispatch(
      postCreateOrUpdateReason({
        ...value,
        id_motiv: reasonId,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postCreateOrUpdateReason) !==
      endpointStatus.pending
    ) {
      setIsSubmitting(false);
    }

    if (
      checkEndpointStatus(resultAction, postCreateOrUpdateReason) ===
      endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const reasonTypesDropdownData = useMemo(
    () =>
      administrateReasonsTypes.map((administrateReason) => ({
        id: administrateReason.value,
        label: administrateReason.label,
      })),
    [],
  );

  const operationsTypesDropdownData = useMemo(
    () =>
      operationsTypes.map((operation) => ({
        id: operation.value,
        label: operation.label,
      })),
    [],
  );

  const title = reasonId
    ? `Editeaza informatii motiv: "${formikValues.denumire || ""}"`
    : "Adauga informatii motiv";

  return (
    <ContentCard
      cardTitle={title}
      cardHeaderClassName="text-center"
      isLoading={isLoading}
      CardHeader={() => <></>}>
      <Formik
        enableReinitialize
        initialValues={formikValues}
        validationSchema={validationSchema}
        onSubmit={handleSaveReasonDetails}>
        {({
          isValid,
          errors,
          touched,
          values,
          getFieldProps,
          handleSubmit,
          setFieldValue,
        }) => {
          const {
            selectedReasonType,
            selectedOperationType,
            showProvider,
            showReceiver,
          } = values;

          const selectedType = reasonTypesDropdownData.find(
            (reasonType) => reasonType.id === selectedReasonType,
          );

          const selectedOperation = operationsTypesDropdownData.find(
            (operationType) => operationType.id === selectedOperationType,
          );

          return (
            <form className="w-full" onSubmit={handleSubmit} noValidate>
              <TextInput
                type="text"
                label="Denumire"
                name="denumire"
                error={errors["denumire"]}
                touched={touched["denumire"]}
                inputProps={getFieldProps("denumire")}
                withVerticalSpacer
              />

              <Dropdown
                className="mb-4 mr-2"
                title="Selecteaza tipul"
                data={reasonTypesDropdownData}
                singleValue={selectedType}
                setSingleValue={(value) => {
                  setFieldValue("selectedReasonType", value?.id);
                }}
              />

              <Dropdown
                className="mb-4 mr-2"
                title="Selecteaza operatie"
                data={operationsTypesDropdownData}
                singleValue={selectedOperation}
                setSingleValue={(value) => {
                  setFieldValue("selectedOperationType", value?.id);
                }}
              />

              <div className="flex flex-wrap gap-6">
                <Checkbox
                  name="showReceiver"
                  label="Vizibil destinatar"
                  checked={showReceiver}
                  onChange={(value) => setFieldValue("showReceiver", value)}
                />
                <Checkbox
                  className="mr-16"
                  name="showProvider"
                  label="Vizibil expeditor"
                  checked={showProvider}
                  onChange={(value) => setFieldValue("showProvider", value)}
                />
              </div>

              <FormButtons
                goBackLabel="Anuleaza"
                goNextLabel="Salveaza"
                handleGoBack={handleCancel}
                nextButtonDisabled={isLoading || isSubmitting || !isValid}
                nextButtonLoading={isSubmitting}
              />
            </form>
          );
        }}
      </Formik>
    </ContentCard>
  );
}

export default CreateOrUpdateReason;
